import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Loader2, ShieldCheck, Award } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export function PrincipalCertificateCard() {
  const [data, setData] = useState(null);
  const [name, setName] = useState('');
  const [designation, setDesignation] = useState('Principal');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/coordinator/school/principal').then(r => {
      setData(r.data);
      setName(r.data.principal_name || '');
      setDesignation(r.data.principal_designation || 'Principal');
    }).catch(() => {});
  }, []);

  const save = async (payload, successMsg) => {
    setSaving(true);
    try {
      const r = await api.put('/coordinator/school/principal', payload);
      setData(d => ({ ...d, ...r.data }));
      toast.success(successMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;
  const locked = !data.can_approve;
  const approved = data.principal_cert_approved;

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] p-5" data-testid="principal-certificate-card">
      <div className="flex items-start gap-2">
        <Award className="h-4 w-4 text-[#F97D6A] mt-0.5 shrink-0" />
        <div>
          <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit']">Mentor Recognition Certificates</h3>
          <p className="text-sm text-[#6B7280] mt-0.5">
            The principal's name appears on mentor recognition certificates issued by your school.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <div>
          <label className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Principal Name</label>
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={locked}
            placeholder="e.g. Dr. Anjali Mehta"
            className="mt-1 rounded-lg text-sm border-[#E5E7EB]"
            data-testid="principal-name-input"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Designation</label>
          <Input
            value={designation}
            onChange={e => setDesignation(e.target.value)}
            disabled={locked}
            placeholder="Principal"
            className="mt-1 rounded-lg text-sm border-[#E5E7EB]"
            data-testid="principal-designation-input"
          />
        </div>
      </div>

      {!locked && (
        <Button
          variant="outline"
          size="sm"
          disabled={saving}
          onClick={() => save({ principal_name: name, principal_designation: designation }, 'Principal details saved')}
          className="mt-3 h-8 text-xs rounded-lg border-[#E5E7EB]"
          data-testid="principal-save-btn"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save details'}
        </Button>
      )}

      <div className="mt-5 pt-4 border-t border-[#F3F4F6] flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-[#1F2937] flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#9CA3AF]" />
            Approve use on certificates
            {approved && <Badge className="border-0 bg-emerald-50 text-emerald-600 text-[10px]" data-testid="principal-approved-badge">Approved</Badge>}
          </p>
          <p className="text-sm text-[#6B7280] mt-1 max-w-md" data-testid="principal-approval-note">
            {approved
              ? `Approved by ${data.principal_cert_approved_by || 'the school'} — certificates are issued with this name.`
              : 'Until this is approved, mentors only see a preview with a placeholder in place of the name.'}
          </p>
          {locked && (
            <p className="text-xs text-amber-600 mt-2" data-testid="principal-locked-note">
              Only your school's authorised administrator can change this.
            </p>
          )}
        </div>
        <Switch
          checked={!!approved}
          disabled={locked || saving}
          onCheckedChange={v => save({ principal_name: name, principal_designation: designation, principal_cert_approved: v },
            v ? 'Approved for mentor certificates' : 'Approval withdrawn')}
          data-testid="principal-approval-switch"
        />
      </div>
    </div>
  );
}
